import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import customDateFormat from "dayjs/plugin/customParseFormat";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as actions from "../actions";

import { Badge, Col, Container, Row } from "reactstrap";
import ReportTable from "components/Report";
import ToggleSwitch from "components/ToggleSwitch";
import GlobalModal from "components/Modal";
import { useGlobalContext } from "contexts/GlobalContext";
import { MdOpenInNew } from "react-icons/md";
import { AiOutlineReload } from "react-icons/ai";
import ls from "Localization";
import onboardHirer from "types/onboardHirer";
import * as api from "services/api";
import { isInRole } from "helpers/auth";

dayjs.extend(customDateFormat);

const AdminCompany = ({ history, companyCnpj, companyId }) => {
	const dispatch = useDispatch();
	const { token, user: currentUser } = useSelector(c => c.login);
	const canManageNotificationPreference = isInRole(currentUser, ["admin"]);
	const { showModal, hideModal, modalVisible } = useGlobalContext();

	const [adminUsers, setAdminUsers] = useState([]);
	const [onboard, setOnboard] = useState({});
	const [loading, setLoading] = useState(false);
	const [submittingId, setSubmittingId] = useState(null);
	const [confirmTarget, setConfirmTarget] = useState(null);
	const submittingRef = useRef(false);
	const currentTargetRef = useRef(null);

	const updateConfirmTarget = target => {
		currentTargetRef.current = target;
		setConfirmTarget(target);
	};

	const createUpdateOnboard = () => {
		dispatch(actions.createUpdateOnboard(companyCnpj));
		history.go(0);
	};

	const userBadge = id => {
		return (
			<div className="d-flex justify-content-center w-100">
				<Badge
					style={{
						cursor: "pointer"
					}}
					color={"primary"}
					target={"_blank"}
					onClick={() => {
						let win = window.open(`/client/${id}`, "_blank");
						win.focus();
					}}
				>
					Usuário <MdOpenInNew size={16} />
				</Badge>
			</div>
		);
	};

	async function getAdminUsers() {
		setLoading(true);
		try {
			const response = await api.sendGet(
				`/v2/onboard/admin-users/${companyId}`,
				{
					Authorization: "Bearer " + token
				}
			);

			const result = await response.json();

			if (result.isValid) {
				setAdminUsers(result?.model?.adminUsers);
				setOnboard(result?.model?.onboardInfo[0]);
			}
		} catch (error) {
			console.log(error);
		}
		setLoading(false);
	}

	useEffect(() => {
		getAdminUsers();
	}, []);

	useEffect(() => {
		if (!modalVisible && !submittingRef.current && currentTargetRef.current) {
			updateConfirmTarget(null);
		}
	}, [modalVisible]);

	const isReceivingNotifications = admin =>
		admin?.receivePendingNotifications !== false;

	const confirmToggle = async () => {
		const admin = confirmTarget;

		if (!admin || submittingRef.current) {
			return;
		}

		if (!isInRole(currentUser, ["admin"])) {
			toast.error("Você não tem permissão para alterar essa preferência.");
			updateConfirmTarget(null);
			hideModal();
			return;
		}

		const nextValue = !isReceivingNotifications(admin);

		submittingRef.current = true;
		setSubmittingId(admin._id);

		try {
			const response = await api.sendPut(
				`/user/${admin._id}/pending-notifications-preference`,
				{
					receivePendingNotifications: nextValue
				},
				{
					Authorization: "Bearer " + token
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const result = await response.json();

			if (result.isValid) {
				setAdminUsers(prev =>
					prev.map(a =>
						a._id === admin._id
							? { ...a, receivePendingNotifications: nextValue }
							: a
					)
				);
				toast.success(
					nextValue
						? "Notificação de contratos pendentes reativada."
						: "Notificação de contratos pendentes desativada."
				);
			} else {
				toast.error(
					result.errors?.message ||
						result.errors?.default ||
						"Houve um erro ao processar a ação."
				);
			}
		} catch (error) {
			console.log(error);
			toast.error("Erro de conexão.");
		}

		submittingRef.current = false;
		setSubmittingId(null);
		if (currentTargetRef.current?._id === admin._id) {
			updateConfirmTarget(null);
			hideModal();
		}
	};

	const renderNotificationToggle = admin => {
		const active = isReceivingNotifications(admin);
		const label = active ? "ATIVO" : "DESATIVADO";

		if (!canManageNotificationPreference) {
			return (
				<div className="d-flex justify-content-center w-100">
					<span className={active ? "" : "text-muted"}>{label}</span>
				</div>
			);
		}

		return (
			<div className="d-flex align-items-center justify-content-center w-100">
				<ToggleSwitch
					id={`notification-toggle-${admin._id}`}
					name={`notification-toggle-${admin._id}`}
					checked={active}
					disabled={submittingId === admin._id}
					onChange={() => {
						updateConfirmTarget(admin);
						showModal();
					}}
				/>
				<span
					className={"ml-2 " + (active ? "" : "text-muted")}
					style={{ lineHeight: "18px" }}
				>
					{label}
				</span>
			</div>
		);
	};

	const handleCancelToggle = () => {
		if (submittingRef.current) {
			return;
		}
		updateConfirmTarget(null);
		hideModal();
	};
	const isSubmitting = confirmTarget
		? submittingId === confirmTarget._id
		: false;

	return (
		<Container className="dashboard">
			<Row className="">
				<Col className="mt-3" xs={12} sm={12} md={6} xl={4}>
					<h4>
						<strong>Usuários Administradores</strong>
					</h4>
				</Col>

				<Col className="mt-3" xs={12} sm={12} md={6} xl={4}>
					<Badge
						style={{
							cursor: "pointer"
						}}
						color={"primary"}
						onClick={createUpdateOnboard}
					>
						<AiOutlineReload /> Reload
					</Badge>
				</Col>
			</Row>
			<Row>
				<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
					<p>
						<strong>Inicio do Processo: </strong>
						<br />
						<span>
							{dayjs(onboard?.createdAt).format(ls.dateFormatWithoutSeconds)}
						</span>
					</p>
				</Col>

				<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
					<p>
						<strong>Status: </strong>
						<br />
						<span>
							<Badge color={"primary"}>{onboardHirer[onboard?.status]}</Badge>
						</span>
					</p>
				</Col>
			</Row>
			<Row>
				<Col className="mt-3" xs={12} sm={12} md={12} xl={12}>
					<ReportTable
						manual
						noExport={true}
						title={"Usuários Admin"}
						data={adminUsers}
						loading={loading}
						pageSize={adminUsers?.length}
						count={adminUsers?.length}
						showPagination={false}
						defaultSorted={[
							{
								id: "status",
								desc: false
							}
						]}
						columns={[
							{
								Header: "Name",
								filterable: false,
								id: "name",
								accessor: c => (c?.name ? c?.name : "--")
							},
							{
								Header: "Email",
								filterable: false,
								id: "email",
								accessor: c => (c?.email ? c?.email : "--"),
								Cell: ({ value }) => (
									<div className="d-flex justify-content-center w-100">
										<span>{value}</span>
									</div>
								)
							},
							{
								Header: (
									<span>
										Receber notificação de <br /> contratos pendentes
									</span>
								),
								filterable: false,
								id: "receivePendingNotifications",
								accessor: c => isReceivingNotifications(c),
								sortable: false,
								Cell: ({ original }) => renderNotificationToggle(original)
							},
							{
								Header: "Ação",
								filterable: false,
								id: "_id",
								accessor: c => (c?._id ? userBadge(c?._id) : "--")
							},
							{
								Header: "Status",
								id: "userStatus",
								accessor: ({ userStatus }) => {
									let content;
									if (userStatus == 1) {
										content = (
											<Badge color={"success"} className={"w-50"}>
												Ativo
											</Badge>
										);
									} else if (userStatus == 0) {
										content = (
											<Badge color={"danger"} className={"w-50"}>
												Inativo
											</Badge>
										);
									} else {
										content = "PENDENTE";
									}

									return (
										<div className="d-flex justify-content-center w-100">
											{content}
										</div>
									);
								}
							}
						]}
					/>
				</Col>
			</Row>
			<Row>
				<Col className="mt-2" xs={12}>
					<p className="text-muted small">
						A opção &quot;Receber notificação de contratos pendentes&quot; vem
						marcada por padrão. Desmarque para que o administrador deixe de
						receber o e-mail — isso não altera o perfil de acesso nem as
						permissões, e a alteração é registrada para auditoria.
					</p>
				</Col>
			</Row>

			<GlobalModal
				title="Confirmar alteração"
				labelButton="Confirmar"
				onOk={confirmToggle}
				onCancel={handleCancelToggle}
				loading={isSubmitting}
			>
				<p>
					Deseja realmente alterar a preferência de notificação de contratos
					pendentes para este usuário?
				</p>
			</GlobalModal>
		</Container>
	);
};

export default AdminCompany;
